import { apiFetch } from "@/lib/api-fetch";
import { SUPABASE_URL } from "@/integrations/supabase/client";
import { uploadToS3, computeFileHash } from "@/lib/s3-upload";
import type {
  FreelancerLinkedInAtual,
  LinkedInBatchSummary,
  LinkedInResumo,
  LinkedInScrapePreview,
  LinkedInSinaisResponse,
  HistoryEntry,
  LinkedInJob,
  LinkedInJobStatus,
} from "@/types/linkedin";

export interface HashMatch {
  job_id: string;
  status: LinkedInJobStatus;
  freelancer_id: string | null;
  original_filename: string | null;
  created_at: string | null;
}

const BASE = SUPABASE_URL.replace(/\/$/, "");

function url(path: string): string {
  return `${BASE}${path}`;
}

export interface ListJobsParams {
  /** 1 status ou vários — array vira `status=a,b,c` (backend usa `in.(...)`). */
  status?: string | string[];
  /** "null" busca os avulsos (batch_id IS NULL). */
  batch_id?: string;
  /** Busca server-side por nome extraído ou arquivo/URL. */
  q?: string;
  /** Fila de revisão: ataca o mais antigo primeiro. */
  mais_antigos_primeiro?: boolean;
  /** Deixa de fora as falhas que ninguém resolve (a busca tenta de novo, ou o perfil não existe). */
  acionaveis?: boolean;
  freelancer_id?: string;
  limit?: number;
  offset?: number;
}

export interface UploadSingleOptions {
  freelancerId?: string;
  onProgress?: (pct: number) => void;
}

export interface BatchUploadProgress {
  /** Índice do arquivo no batch (0-based). */
  index: number;
  /** Nome do arquivo. */
  filename: string;
  /** Percentual 0-100 do upload pro S3. */
  pct: number;
}

export const linkedInApi = {
  // ---- Upload --------------------------------------------------------------

  /**
   * Sobe 1 PDF: presign → POST direto no S3 → /jobs/confirm na API.
   * onProgress é chamado durante o upload S3 (0-100).
   */
  async uploadSingle(file: File, opts: UploadSingleOptions = {}): Promise<LinkedInJob> {
    const [{ s3_key, original_filename }, file_hash] = await Promise.all([
      uploadToS3({ kind: "linkedin_pdf", file, onProgress: opts.onProgress }),
      computeFileHash(file),
    ]);
    return apiFetch<LinkedInJob>(url("/linkedin/jobs/confirm"), {
      method: "POST",
      body: JSON.stringify({
        s3_key,
        original_filename,
        freelancer_id: opts.freelancerId,
        file_hash,
      }),
    });
  },

  /**
   * Sobe N PDFs em paralelo pro S3, depois chama /jobs/confirm-batch.
   * onProgress por arquivo (opcional) pra UI mostrar progresso individual.
   * Quando `deferred=true`, os jobs ficam em `pending` sem disparar extração
   * imediatamente — o cliente depois chama `processBatch(jobIds)` pra iniciar.
   */
  async uploadBatch(
    files: File[],
    onProgress?: (p: BatchUploadProgress) => void,
    opts: { deferred?: boolean } = {},
  ): Promise<{ jobs: LinkedInJob[]; total: number }> {
    const uploaded = await Promise.all(
      files.map(async (file, index) => {
        const [res, file_hash] = await Promise.all([
          uploadToS3({
            kind: "linkedin_pdf",
            file,
            onProgress: (pct) => onProgress?.({ index, filename: file.name, pct }),
          }),
          computeFileHash(file),
        ]);
        return { ...res, file_hash };
      }),
    );
    const qs = opts.deferred ? "?processing_deferred=true" : "";
    return apiFetch(url(`/linkedin/jobs/confirm-batch${qs}`), {
      method: "POST",
      body: JSON.stringify({
        items: uploaded.map((u) => ({
          s3_key: u.s3_key,
          original_filename: u.original_filename,
          file_hash: u.file_hash,
        })),
      }),
    });
  },

  /**
   * Dispara `run_extraction` para uma lista de jobs em `pending` (usado em
   * conjunto com `uploadBatch({deferred:true})`). Retorna lista de processados
   * e de falhas com motivo — feedback de parciais explícito.
   */
  processBatch(
    jobIds: string[],
  ): Promise<{ processed: string[]; failed: { job_id: string; reason: string }[] }> {
    return apiFetch(url("/linkedin/jobs/process-batch"), {
      method: "POST",
      body: JSON.stringify({ job_ids: jobIds }),
    });
  },

  /**
   * Inicia a busca automática dos perfis públicos de LinkedIn. Sem
   * `freelancerIds`, alcança todos os freelancers com URL de perfil válida;
   * quem já tem job em andamento ou revisão pendente é pulado. Os jobs criados
   * entram no mesmo fluxo dos uploads (extração, revisão, aplicação).
   */
  scrapeBatch(
    freelancerIds?: string[],
    limit?: number,
  ): Promise<{
    batch_id: string | null;
    created: number;
    skipped: { freelancer_id: string; reason: string }[];
  }> {
    return apiFetch(url("/linkedin/jobs/scrape-batch"), {
      method: "POST",
      body: JSON.stringify({
        freelancer_ids: freelancerIds ?? null,
        limit: limit ?? null,
      }),
    });
  },

  /** Contagens globais do cabeçalho. */
  resumo(): Promise<LinkedInResumo> {
    return apiFetch(url("/linkedin/resumo"));
  },

  /** Aplica vários jobs de uma vez (o que já está pronto pra aplicar). */
  applyBatch(
    jobIds: string[],
  ): Promise<{ aplicados: string[]; falhas: { job_id: string; reason: string }[] }> {
    return apiFetch(url("/linkedin/jobs/apply-batch"), {
      method: "POST",
      body: JSON.stringify({ job_ids: jobIds }),
    });
  },

  /** Quantos perfis entrariam numa busca automática agora (pro dialog). */
  scrapePreview(): Promise<LinkedInScrapePreview> {
    return apiFetch(url("/linkedin/scrape-preview"));
  },

  /**
   * Recebe hashes (SHA-256) e retorna quais já existem em jobs anteriores.
   * Usado pra marcar PDFs já processados antes de submeter o lote.
   */
  checkHashes(hashes: string[]): Promise<{ matches: Record<string, HashMatch> }> {
    return apiFetch(url("/linkedin/jobs/check-hashes"), {
      method: "POST",
      body: JSON.stringify({ hashes }),
    });
  },

  // ---- Consulta ------------------------------------------------------------

  listJobs(params: ListJobsParams = {}): Promise<{ jobs: LinkedInJob[]; total: number }> {
    const q = new URLSearchParams();
    if (params.status) {
      q.set("status", Array.isArray(params.status) ? params.status.join(",") : params.status);
    }
    if (params.batch_id) q.set("batch_id", params.batch_id);
    if (params.q) q.set("q", params.q);
    if (params.mais_antigos_primeiro) q.set("mais_antigos_primeiro", "true");
    if (params.acionaveis) q.set("acionaveis", "true");
    if (params.freelancer_id) q.set("freelancer_id", params.freelancer_id);
    q.set("limit", String(params.limit ?? 50));
    q.set("offset", String(params.offset ?? 0));
    return apiFetch(url(`/linkedin/jobs?${q.toString()}`));
  },

  /** Lotes agregados (contagens) pra listagem principal — leve. */
  listBatches(
    params: { limit?: number; offset?: number } = {},
  ): Promise<{ batches: LinkedInBatchSummary[]; total: number }> {
    const q = new URLSearchParams();
    q.set("limit", String(params.limit ?? 15));
    q.set("offset", String(params.offset ?? 0));
    return apiFetch(url(`/linkedin/batches?${q.toString()}`));
  },

  /** Mudanças vistas entre duas buscas seguidas do mesmo perfil, mais recentes primeiro. */
  listSinais(
    params: { dias?: number; apenasQuentes?: boolean; limit?: number; offset?: number } = {},
  ): Promise<LinkedInSinaisResponse> {
    const q = new URLSearchParams();
    q.set("dias", String(params.dias ?? 30));
    q.set("apenas_quentes", String(params.apenasQuentes ?? true));
    q.set("limit", String(params.limit ?? 50));
    q.set("offset", String(params.offset ?? 0));
    return apiFetch(url(`/linkedin/sinais?${q.toString()}`));
  },

  getJob(id: string): Promise<LinkedInJob> {
    return apiFetch(url(`/linkedin/jobs/${id}`));
  },

  // ---- Ações no job --------------------------------------------------------

  associate(jobId: string, freelancerId: string): Promise<LinkedInJob> {
    return apiFetch(url(`/linkedin/jobs/${jobId}/associate`), {
      method: "POST",
      body: JSON.stringify({ freelancer_id: freelancerId }),
    });
  },

  apply(
    jobId: string,
    body: { overrides?: Record<string, unknown>; force?: boolean } = {},
  ): Promise<LinkedInJob> {
    return apiFetch(url(`/linkedin/jobs/${jobId}/apply`), {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  reextract(jobId: string): Promise<LinkedInJob> {
    return apiFetch(url(`/linkedin/jobs/${jobId}/reextract`), { method: "POST" });
  },

  cancel(jobId: string): Promise<LinkedInJob> {
    return apiFetch(url(`/linkedin/jobs/${jobId}/cancel`), { method: "POST" });
  },

  // ---- Histórico / estado atual --------------------------------------------

  getFreelancerAtual(freelancerId: string): Promise<FreelancerLinkedInAtual | null> {
    return apiFetch(url(`/freelancers/${freelancerId}/linkedin`));
  },

  listFreelancerHistory(freelancerId: string): Promise<HistoryEntry[]> {
    return apiFetch(url(`/freelancers/${freelancerId}/linkedin-history`));
  },

  getHistoryEntry(historyId: string): Promise<HistoryEntry> {
    return apiFetch(url(`/linkedin/history/${historyId}`));
  },
};
